import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Anchor, Badge, Box, Group, Loader, SegmentedControl, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Cpu, Info } from 'lucide-react';
import RankedList, { type RankedListColumn } from '@/design/RankedList/RankedList';
import { type StatItem } from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import { accentTokens } from '@/design/VizAccents';
import { formatCount, formatPercent, formatTokens, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import DerivedInsightsPanel from '@/shared/surfaces/DerivedInsightsPanel';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import { useGlobalFilterKey } from '@/features/global-filters/useGlobalFilters';
import { useSurface } from '@/shared/surfaces/useSurface';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { BehaviorModelFitViewModel, type ModelFitRow, type ModelFitSnapshot, type ModelFitUserRow } from './BehaviorModelFitViewModel';

/** A high page size so the ranked lists never paginate — they scroll inside RankedList's own container instead. */
const NO_PAGINATION = 1000;
type FitView = 'users' | 'models';

/** Best-available fit for the bar: the classified avg fit score (1–100), falling back to a tier×difficulty proxy until classification has scored this window. */
function displayFit(classifiedFitScore: number, misfitShare: number): number {
    return classifiedFitScore > 0 ? classifiedFitScore : Math.round((1 - misfitShare) * 100);
}

/** Behavior › Model Fit. Whether work goes to the right-sized model — who routes correctly, which models carry the right jobs, and the spend wasted on tier-mismatched prompts. */
export default function BehaviorModelFitPage(): ReactElement {
    const surface = useSurface('behavior');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <ModelFitBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function ModelFitBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(BehaviorModelFitViewModel);
    const ambientFilters = useAssistFilters();
    const globalKey = useGlobalFilterKey();
    const filters = useMemo(() => [...ambientFilters], [ambientFilters]);

    useEffect(() => {
        void vm.load(filters);
    }, [vm, filters, globalKey]);

    const header = (
        <>
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />
            <PageHeaderComponent
                icon={Cpu}
                eyebrow="Behavior"
                title={
                    <Group gap="xs" align="center" wrap="nowrap">
                        Model Fit
                        <Tooltip label="Model fit scores are still a work in progress — treat them as directional." multiline w={250} withArrow position="right">
                            <Badge
                                variant="light"
                                radius="sm"
                                styles={{ root: { cursor: 'help', background: 'var(--bm-color-brand-soft)', color: 'var(--bm-color-brand-accent)', border: '1px solid var(--bm-color-brand-border)' } }}
                            >
                                Beta
                            </Badge>
                        </Tooltip>
                    </Group>
                }
                subtitle="Whether each job goes to the right-sized model — who routes work correctly, and the spend wasted on prompts sent to a tier-mismatched model."
            />
        </>
    );

    if (vm.errorMessage !== null) {
        return (
            <Box pb="xl">
                {header}
                <Text c="red" mt="md" size="sm">
                    {vm.errorMessage}
                </Text>
            </Box>
        );
    }
    if (vm.snapshot === null) {
        return (
            <Box pb="xl">
                {header}
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Loading model fit…
                    </Text>
                </Group>
            </Box>
        );
    }
    if (vm.snapshot.classifiedTurns === 0) {
        return (
            <Box pb="xl">
                {header}
                <EmptyFit />
            </Box>
        );
    }

    return (
        <Box pb="xl">
            {header}
            <ModelFitContent snapshot={vm.snapshot} basePath={surface.basePath} />
        </Box>
    );
}

function ModelFitContent({ snapshot, basePath }: { snapshot: ModelFitSnapshot; basePath: string }): ReactElement {
    const [view, setView] = useState<FitView>('users');
    const hasCost = snapshot.totalCost > 0;

    const railStats: StatItem[] = [
        {
            label: 'Fit score',
            value: `${snapshot.fitScore}`,
            hint: snapshot.fitScoreFromClassification ? 'avg fit, 1–100' : 'tier × difficulty',
            info: snapshot.fitScoreFromClassification
                ? 'Average per-interaction model-fit score from classification (1–100) — how well each model suited its task difficulty.'
                : 'Transitional estimate from model tier vs task difficulty, shown until classification has scored this window.',
        },
    ];
    if (hasCost) {
        railStats.push({ label: 'Waste', value: formatUsd(snapshot.wasteCost), hint: 'tier-mismatched spend', info: 'Allocated cost of prompts sent to a tier-mismatched model — premium on easy work, or a lighter model on hard work.' });
    }
    railStats.push(
        { label: 'Over-provisioned', value: formatPercent(snapshot.overProvisionedShare), hint: 'premium on easy work' },
        { label: 'Under-provisioned', value: formatPercent(snapshot.underProvisionedShare), hint: 'lighter model on hard work' },
    );

    const rail = (
        <Stack gap="md">
            <TopicSummaryPanel stats={railStats} />
            <DerivedInsightsPanel callouts={buildInsights(snapshot, hasCost)} accent="indigo" />
        </Stack>
    );

    return (
        <SideRailPageLayout rail={rail}>
            <Stack gap="xl">
                <Stack gap="sm">
                    <Group justify="space-between" align="center" gap="sm">
                        <SegmentedControl
                            size="xs"
                            value={view}
                            onChange={(value) => setView(value === 'models' ? 'models' : 'users')}
                            data={[
                                { value: 'users', label: 'Users' },
                                { value: 'models', label: 'Models' },
                            ]}
                            w="fit-content"
                        />
                        <Text size="xs" c="dimmed" ta="right" visibleFrom="sm">
                            {view === 'users' ? 'The bar is each person’s avg fit score. Open one for their prompts →' : 'The bar is each model’s avg fit score. Open one for its prompts →'}
                        </Text>
                    </Group>
                    {view === 'users' ?
                        <UsersFitList users={snapshot.userRows} basePath={basePath} hasCost={hasCost} />
                    :   <ModelsFitList models={snapshot.models} basePath={basePath} hasCost={hasCost} />}
                </Stack>

                <Text size="xs" c="dimmed">
                    Wondering where each model’s work actually lands?{' '}
                    <Anchor component={Link} to={`${basePath}/model-by-task`} size="xs">
                        See the full models-by-task matrix →
                    </Anchor>
                </Text>
            </Stack>
        </SideRailPageLayout>
    );
}

/** People ranked by avg fit score — the bar is their fit. Each row opens their prompt grid. */
function UsersFitList({ users, basePath, hasCost }: { users: ModelFitUserRow[]; basePath: string; hasCost: boolean }): ReactElement {
    const columns: RankedListColumn<ModelFitUserRow>[] = [];
    if (hasCost) {
        columns.push({ key: 'waste', header: 'Waste', width: 80, render: (u) => (u.wasteCost > 0 ? formatUsd(u.wasteCost) : '—'), sort: { get: (u) => u.wasteCost, initialDir: 'desc' } });
    }
    columns.push({ key: 'turns', header: 'Prompts', width: 76, size: 'xs', visibleFrom: 'md', render: (u) => formatCount(u.classifiedTurns), sort: { get: (u) => u.classifiedTurns, initialDir: 'desc' } });

    return (
        <RankedList
            rows={users}
            accent="indigo"
            rowKey={(u) => u.userId}
            unitPlural="people"
            initialSort={{ key: 'person', dir: 'desc' }}
            primary={{
                key: 'person',
                header: 'Person',
                label: (u) => humanUser(u.userId),
                subline: (u) => `${formatCount(u.overTurns)} over · ${formatCount(u.underTurns)} under`,
                fill: (u) => displayFit(u.classifiedFitScore, u.misfitShare),
                share: (u) => `${displayFit(u.classifiedFitScore, u.misfitShare)}`,
                sort: { get: (u) => displayFit(u.classifiedFitScore, u.misfitShare), initialDir: 'desc' },
            }}
            columns={columns}
            hrefFor={(u) => `${basePath}/model-fit/user/${encodeURIComponent(u.userId)}`}
            pageSize={NO_PAGINATION}
        />
    );
}

/** Models ranked by avg fit score — the bar is their fit. Each row opens its prompt grid. */
function ModelsFitList({ models, basePath, hasCost }: { models: ModelFitRow[]; basePath: string; hasCost: boolean }): ReactElement {
    const columns: RankedListColumn<ModelFitRow>[] = [
        { key: 'turns', header: 'Prompts', width: 76, size: 'xs', visibleFrom: 'sm', render: (m) => formatCount(m.classifiedTurns), sort: { get: (m) => m.classifiedTurns, initialDir: 'desc' } },
        { key: 'tokens', header: 'Tokens', width: 84, size: 'xs', visibleFrom: 'md', render: (m) => formatTokens(m.totalTokens), sort: { get: (m) => m.totalTokens, initialDir: 'desc' } },
    ];
    if (hasCost) {
        columns.push({ key: 'cost', header: 'Spend', width: 80, size: 'xs', visibleFrom: 'md', render: (m) => formatUsd(m.cost), sort: { get: (m) => m.cost, initialDir: 'desc' } });
    }

    return (
        <RankedList
            rows={models}
            accent="indigo"
            rowKey={(m) => m.model}
            unitPlural="models"
            initialSort={{ key: 'model', dir: 'desc' }}
            primary={{
                key: 'model',
                header: 'Model',
                label: (m) => m.model,
                subline: (m) => (m.tier === 'premium' ? 'Premium tier' : 'Standard tier'),
                fill: (m) => displayFit(m.classifiedFitScore, m.misfitShare),
                share: (m) => `${displayFit(m.classifiedFitScore, m.misfitShare)}`,
                sort: { get: (m) => displayFit(m.classifiedFitScore, m.misfitShare), initialDir: 'desc' },
            }}
            columns={columns}
            hrefFor={(m) => `${basePath}/model-fit/model/${encodeURIComponent(m.model)}`}
            pageSize={NO_PAGINATION}
        />
    );
}

function EmptyFit(): ReactElement {
    const accent = accentTokens('indigo');
    return (
        <SurfaceCard $padding="lg">
            <Group gap={12} align="flex-start" wrap="nowrap">
                <ThemeIcon size={32} radius="xl" style={{ background: accent.soft, color: accent.text }}>
                    <Info size={17} />
                </ThemeIcon>
                <Box>
                    <Text fw={600} mb={4}>
                        No classified difficulty in this window
                    </Text>
                    <Text size="sm" c="dimmed" style={{ lineHeight: 1.55 }}>
                        Model fit needs prompts tagged with a task difficulty. Widen the date range, or wait for classification to
                        finish on this period&apos;s usage.
                    </Text>
                </Box>
            </Group>
        </SurfaceCard>
    );
}

/** Derived takeaways for the rail: lead with overall fit + waste, then the biggest over-provisioning (avoidable spend) and under-provisioning (quality risk). */
function buildInsights(snapshot: ModelFitSnapshot, hasCost: boolean): TopicCallout[] {
    const callouts: TopicCallout[] = [];
    callouts.push({
        tone: 'headline',
        text:
            hasCost && snapshot.wasteCost > 0 ?
                `Overall model-to-task fit is ${snapshot.fitScore}/100, with ${formatUsd(snapshot.wasteCost)} spent on tier-mismatched prompts.`
            :   `Overall model-to-task fit is ${snapshot.fitScore}/100.`,
    });

    const topOver = snapshot.models.filter((model) => model.tier === 'premium' && model.misfitTurns > 0).sort((a, b) => b.misfitTurns - a.misfitTurns)[0];
    if (topOver !== undefined) {
        callouts.push({
            tone: 'opportunity',
            text: `Route ${topOver.model}’s easy work to a lighter model — ${formatPercent(topOver.misfitShare)} of its prompts (${formatTokens(topOver.misfitTokens)} tokens) are low-difficulty.`,
        });
    }

    const topWasteUser = snapshot.userRows.find((user) => user.wasteCost > 0);
    if (hasCost && topWasteUser !== undefined) {
        callouts.push({
            tone: 'opportunity',
            text: `${humanUser(topWasteUser.userId)} drives the most waste — ${formatUsd(topWasteUser.wasteCost)} on tier-mismatched prompts.`,
        });
    }

    const topUnder = snapshot.models.filter((model) => model.tier === 'standard' && model.misfitTurns > 0).sort((a, b) => b.misfitTurns - a.misfitTurns)[0];
    if (topUnder !== undefined) {
        callouts.push({
            tone: 'watch',
            text: `${topUnder.model} is handling harder work than its tier suits — ${formatPercent(topUnder.misfitShare)} of its prompts are medium/high difficulty, where a stronger model can cut re-prompts.`,
        });
    }

    if (callouts.length === 1) {
        callouts.push({ tone: 'headline', text: 'Model routing looks well-matched to task difficulty this period — no major over- or under-provisioning to act on.' });
    }
    return callouts;
}
